// @flow


import { MEDIA_TYPE, VIDEO_TYPE } from '../../../../react/features/base/media/constants';
import {
    getDominantSpeakerParticipant,
    getLocalParticipant,
    getPinnedParticipant,
    getRemoteParticipants,
    getParticipantById
} from '../../../../react/features/base/participants/functions';
import { toState } from '../../../../react/features/base/redux/functions';
import { setVisibleRemoteParticipants } from '../../../../react/features/filmstrip/actions';
import { shouldDisplayTileView } from '../../../../react/features/video-layout/functions';
import type { Dispatch } from 'redux';


import {
    WIZY_ACTIVE_SPEAKER_ADDED,
    WIZY_ACTIVE_SPEAKER_REMOVED,
    WIZY_PINNED_PARTICIPANT,
    WIZY_SELECT_LARGE_VIDEO_PARTICIPANT
} from './actionTypes';


declare var APP: Object;

export function wizyPinParticipant(enabled) {
    return {
        type: WIZY_PINNED_PARTICIPANT,
        payload: enabled
    };
}

export function wizyActiveSpeakerAdded(speaker) {
    return {
        type: WIZY_ACTIVE_SPEAKER_ADDED,
        speaker
    };
}

export function wizyActiveSpeakerRemoved(speaker) {
    return {
        type: WIZY_ACTIVE_SPEAKER_REMOVED,
        speaker
    };
}

/**
 * Signals conference to select a participant.
 *
 * @returns {Function}
 */
export function wizySelectParticipant() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const { conference } = state['features/base/conference'];

        if (conference) {
            const ids = shouldDisplayTileView(state)
                ? state['features/filmstrip'].remoteParticipants
                : state['wizyconf/large-video'].participantIds;

            dispatch(setVisibleRemoteParticipants(0, ids?.length));
        }
    };
}

/**
 * Action to select the participant to be displayed in LargeVideo based on a
 * variety of factors: If there is a dominant or pinned speaker, or if there are
 * remote tracks, etc.
 *
 * @returns {Function}
 */
export function wizySelectParticipantInLargeVideo(participant: ?string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const participantIds = _electParticipantInLargeVideo(state, participant);
        const largeVideo = state['wizyconf/large-video'];
        const largeVideoParticipantIds = largeVideo.participantIds.filter(Boolean);
        const { remoteScreenShares = [] } = state['features/video-layout'];
        let latestScreenshareParticipantId;

        if (remoteScreenShares.length) {
            latestScreenshareParticipantId = remoteScreenShares[remoteScreenShares.length - 1];
        }

        if (largeVideoParticipantIds.length !== participantIds.size
            || participantIds.has(latestScreenshareParticipantId)
            || !largeVideoParticipantIds.every(id => participantIds.has(id))) {

            dispatch({
                type: WIZY_SELECT_LARGE_VIDEO_PARTICIPANT,
                participantIds: Array.from(participantIds),
                remoteScreenShares: [ ...remoteScreenShares ].reverse()
            });

            dispatch(wizySelectParticipant());
        }
    };
}


export function wizyRemoveParticipantFromLargeVideo(participantId) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const largeVideo = state['wizyconf/large-video'];

        if (largeVideo.participantIds.includes(participantId)) {

            dispatch(wizySelectParticipantInLargeVideo());
        }
    };
}

/**
 * Returns the most recent existing remote video track.
 *
 * @param {Track[]} tracks - All current tracks.
 * @param {Number} desiredTracksCount - Desired number of tracks in
 * the resulting array.
 * @private
 * @returns {(Track[]|[])}
 */
function _electLastVisibleRemoteParticipant(stateful, desiredTracksCount) {
    const state = toState(stateful);
    const tracks = state['features/base/tracks'];
    const electedParticipants = [];

    // First we try to get most recent remote video tracks.
    for (let i = tracks.length - 1; i >= 0 && electedParticipants.length < desiredTracksCount; --i) {
        const track = tracks[i];

        if (!track.local
            && track.mediaType === MEDIA_TYPE.VIDEO
            && track.videoType !== VIDEO_TYPE.DESKTOP
            && track.participantId) {
            const participant = getParticipantById(state, track.participantId);

            if (participant) {
                electedParticipants.push(participant);
            }
        }
    }

    return electedParticipants;
}

/**
 * Returns the identifier of the participant who is to be on the stage and
 * should be displayed in {@code LargeVideo}.
 *
 * @param {Object} state - The Redux state from which the participant to be
 * displayed in {@code LargeVideo} is to be elected.
 * @param {(string|undefined)} participantId - The identifier of the participant
 * who should be on the stage.
 * @private
 * @returns {(string|undefined)}
 */
function _electParticipantInLargeVideo(state, participantId) {
    const electedParticipants = new Set();
    const desiredParticipantsCount = APP.wildix.wizy.screenKeys.length;

    participantId && electedParticipants.add(participantId);

    // 1. If a participant is pinned, they will be shown in the LargeVideo
    // (regardless of whether they are local or remote).
    let participant = getPinnedParticipant(state);

    if (participant) {
        electedParticipants.add(participant.id);

        if (electedParticipants.size === desiredParticipantsCount) {
            return electedParticipants;
        }
    }

    // 2. Next, pick the most recent remote screenshare that was added to the conference.
    const remoteScreenShares = state['features/video-layout'].remoteScreenShares;

    if (remoteScreenShares?.length) {
        for (let i = remoteScreenShares.length - 1; i >= 0; i--) {
            electedParticipants.add(remoteScreenShares[i]);

            if (electedParticipants.size === desiredParticipantsCount) {
                return electedParticipants;
            }
        }
    }

    // 3. Next, pick the dominant speaker (other than self).
    participant = getDominantSpeakerParticipant(state);
    if (participant && !participant.local) {
        electedParticipants.add(participant.id);

        if (electedParticipants.size === desiredParticipantsCount) {
            return electedParticipants;
        }
    }

    // In case this is the local participant.
    participant = undefined;

    // Wizyconf logic
    // 3.1 Next, pick the most recent speakers.
    const { wizySpeakersList } = state['wizyconf/large-video'];
    const activeSpeakers = [ ...wizySpeakersList ];

    for (let i = activeSpeakers.length - 1; i >= 0; i--) {
        electedParticipants.add(activeSpeakers[i]);

        if (electedParticipants.size === desiredParticipantsCount) {
            return electedParticipants;
        }
    }

    // 4. Next, pick the most recent participant with video.
    const desiredTracksCount = desiredParticipantsCount - electedParticipants.size;
    const remoteParticipants = _electLastVisibleRemoteParticipant(state, desiredTracksCount);

    if (remoteParticipants?.length) {
        remoteParticipants.forEach(p => electedParticipants.add(p.id));

        if (electedParticipants.size === desiredParticipantsCount) {
            return electedParticipants;
        }
    }

    // 5. As a last resort, select the participant that joined last (other than poltergist or other bot type
    // participants).

    const participants = [ ...getRemoteParticipants(state).values() ];

    for (let i = participants.length; i > 0; i--) {
        const p = participants[i - 1];

        if (p.botType) {
            continue;
        }

        electedParticipants.add(p.id);

        if (electedParticipants.size === desiredParticipantsCount) {
            return electedParticipants;
        }
    }

    const localParticipantId = getLocalParticipant(state)?.id;

    localParticipantId && electedParticipants.add(localParticipantId);

    return electedParticipants;
}
